import React, { useMemo, useRef, useState, useCallback } from "react";
import { MousePointer2, Minus, Square, Trash2, Undo2, Magnet, RotateCw, DoorOpen, AppWindow, Wind } from "lucide-react";

const F = Number.isFinite;
const snap = (v, on, step = 0.25) => (on ? Math.round(v / step) * step : v);
const WELD = 0.35; // auto-weld radius (m)

// Per-discipline fixture palettes for the MEP / Pool editing toolbars.
// Each entry: [kind, shortCode, color].
const FIXTURE_TOOLS = {
  electrical: [
    ["outlet", "OU", "#F59E0B"], ["outlet_gfci", "GF", "#B45309"],
    ["switch", "SW", "#F59E0B"], ["light_ceiling", "LT", "#EAB308"],
    ["ac_unit", "AC", "#0EA5E9"], ["elec_jb", "JB", "#78350F"],
  ],
  plumbing: [
    ["sink", "SK", "#3B82F6"], ["wc", "WC", "#2563EB"],
    ["shower", "SH", "#0EA5E9"], ["bath_tub", "TB", "#1D4ED8"],
    ["extractor", "EX", "#64748B"],
  ],
  pool: [
    ["pool_skimmer", "SK", "#DC2626"], ["pool_return", "RJ", "#059669"],
    ["pool_drain", "MD", "#DC2626"], ["pool_pump", "PU", "#0891B2"],
    ["pool_filter", "FI", "#0891B2"], ["pool_heater", "HT", "#D97706"],
    ["pool_ladder", "LD", "#2563EB"],
  ],
  structural: [
    ["footing_isolated", "F", "#F59E0B"], ["footing_combined", "CF", "#D97706"],
    ["footing_strip", "SF", "#B45309"], ["column_mark", "C", "#EF4444"],
    ["tie_beam", "TB", "#8B5CF6"], ["main_beam", "BM", "#7C3AED"],
    ["shear_wall", "SW", "#DC2626"],
  ],
};
const FIXTURE_META = Object.values(FIXTURE_TOOLS).flat().reduce((m, [k, code, c]) => { m[k] = { code, c }; return m; }, {});
const DISCIPLINES = [["arch", "🏛️", "Architectural"], ["structural", "🧱", "Structural"], ["electrical", "⚡", "Electrical"], ["plumbing", "🚰", "Plumbing"], ["pool", "🏊", "Pool"]];

function bounds(model, W, H, pad = 40) {
  const walls = model.walls || [];
  let xmin = 0, xmax = 20, ymin = 0, ymax = 15;
  if (walls.length) {
    xmin = Infinity; xmax = -Infinity; ymin = Infinity; ymax = -Infinity;
    walls.forEach((w) => {
      xmin = Math.min(xmin, w.p1.x, w.p2.x); xmax = Math.max(xmax, w.p1.x, w.p2.x);
      ymin = Math.min(ymin, w.p1.y, w.p2.y); ymax = Math.max(ymax, w.p1.y, w.p2.y);
    });
  }
  const bw = (xmax - xmin) || 1, bh = (ymax - ymin) || 1;
  const s = Math.min((W - pad * 2) / bw, (H - pad * 2) / bh);
  return { s, ox: (W - bw * s) / 2 - xmin * s, oy: (H - bh * s) / 2 - ymin * s };
}

function NumField({ label, value, onCommit, step = 0.1, testid }) {
  const [v, setV] = useState(String(value));
  React.useEffect(() => setV(String(value)), [value]);
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type="number" step={step} value={v} data-testid={testid}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { const n = parseFloat(v); if (F(n)) onCommit(n); }}
        onKeyDown={(e) => { if (e.key === "Enter") { const n = parseFloat(v); if (F(n)) onCommit(n); e.target.blur(); } }}
        className="w-20 rounded-md border border-input bg-black/30 px-2 py-1 text-foreground focus:border-primary focus:outline-none"
      />
    </label>
  );
}

/**
 * InteractivePlanEditor - drag wall endpoints / columns, move openings,
 * add/delete elements, exact numeric wall length & thickness, column rotation,
 * grid snap + auto-weld of nearby endpoints, and undo. Emits mutated model.
 */
export default function InteractivePlanEditor({ model, onChange, W = 900, H = 620 }) {
  const svgRef = useRef(null);
  const drag = useRef(null);
  const [tool, setTool] = useState("select");
  const [snapOn, setSnapOn] = useState(true);
  const [sel, setSel] = useState(null);
  const [pending, setPending] = useState(null);
  const [history, setHistory] = useState([]);
  const [discipline, setDiscipline] = useState("arch");
  const [mepTool, setMepTool] = useState("");

  const tf = useMemo(() => bounds(model, W, H), [model, W, H]);
  const px = (x) => x * tf.s + tf.ox;
  const py = (y) => y * tf.s + tf.oy;

  const walls = model.walls || [];
  const columns = model.columns || [];
  const openings = model.openings || [];
  const fixtures = model.mep_fixtures || [];
  const zones = (model.room_labels_pos || []).filter((z) => z && F(z.x) && F(z.y) && z.zone_kind !== "vent");
  const vents = (model.room_labels_pos || []).map((z, i) => ({ v: z, i })).filter(({ v }) => v && F(v.x) && F(v.y) && v.zone_kind === "vent");

  // snap a point to a nearby existing endpoint (auto-weld), excluding one wall index
  const weld = useCallback((pt, exclude) => {
    let best = null, bestD = WELD;
    walls.forEach((w, i) => {
      if (i === exclude) return;
      [w.p1, w.p2].forEach((p) => {
        const d = Math.hypot(p.x - pt.x, p.y - pt.y);
        if (d < bestD) { bestD = d; best = { x: p.x, y: p.y }; }
      });
    });
    return best || pt;
  }, [walls]);

  const toModel = useCallback((e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const sx = (e.clientX - rect.left) * (W / rect.width);
    const sy = (e.clientY - rect.top) * (H / rect.height);
    return { x: snap((sx - tf.ox) / tf.s, snapOn), y: snap((sy - tf.oy) / tf.s, snapOn) };
  }, [tf, W, H, snapOn]);

  const commit = (next) => { setHistory((h) => [...h.slice(-40), model]); queueMicrotask(() => onChange(next)); };
  const undo = () => setHistory((h) => { if (!h.length) return h; onChange(h[h.length - 1]); return h.slice(0, -1); });

  const onDown = (e) => {
    const m = toModel(e);
    // MEP / Pool disciplines: place a fixture on click (delete tool is a no-op
    // on empty canvas - fixtures are removed by clicking them directly).
    if (discipline !== "arch") {
      if (mepTool && mepTool !== "delete") {
        const id = `mf-${Math.random().toString(36).slice(2, 8)}`;
        commit({ ...model, mep_fixtures: [...fixtures, { id, kind: mepTool, x: +m.x.toFixed(2), y: +m.y.toFixed(2), manual: true }] });
      }
      return;
    }
    if (tool === "column") { commit({ ...model, columns: [...columns, { centre: { x: m.x, y: m.y }, width: 0.4, depth: 0.4, angle: 0 }] }); return; }
    if (tool === "door" || tool === "window") {
      // Drop a new opening on the wall nearest the click, at the projected
      // offset. This lets the user ADD a door/window on any empty wall.
      let best = null, bestD = Infinity, bestOff = 0;
      walls.forEach((w) => {
        const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y, L = Math.hypot(dx, dy) || 1;
        const t = Math.max(0, Math.min(1, ((m.x - w.p1.x) * dx + (m.y - w.p1.y) * dy) / (L * L)));
        const projx = w.p1.x + t * dx, projy = w.p1.y + t * dy;
        const d = Math.hypot(m.x - projx, m.y - projy);
        if (d < bestD) { bestD = d; best = w; bestOff = t * L; }
      });
      if (best && bestD <= 1.2) {
        const width = tool === "door" ? 0.9 : 1.2;
        const dx = best.p2.x - best.p1.x, dy = best.p2.y - best.p1.y, L = Math.hypot(dx, dy) || 1;
        const off = Math.max(width / 2, Math.min(L - width / 2, bestOff));
        const id = `o-${Math.random().toString(36).slice(2, 8)}`;
        commit({ ...model, openings: [...openings, { id, wall_id: best.id, offset_from_p1: +off.toFixed(2), width, height: tool === "door" ? 2.1 : 1.2, kind: tool, label: tool === "door" ? "باب" : "نافذة" }] });
      }
      return;
    }
    if (tool === "vent") {
      // Add a shared light-well (منور) - a small void with windows on each
      // side for daylight + ventilation. Default economical 1×1 m.
      commit({ ...model, room_labels_pos: [...(model.room_labels_pos || []), { x: +m.x.toFixed(2), y: +m.y.toFixed(2), w: 1.0, h: 1.0, zone_kind: "vent", label: "منور تهوية · Vent shaft" }] });
      return;
    }
    if (tool === "wall") {
      if (!pending) setPending(m);
      else {
        const id = `w-${Math.random().toString(36).slice(2, 8)}`;
        commit({ ...model, walls: [...walls, { id, p1: weld(pending), p2: weld(m), wall_type: "interior", thickness: 0.15 }] });
        setPending(null);
      }
    }
  };
  const startDrag = (payload, e) => { e.stopPropagation(); if (tool !== "select") return; drag.current = payload; setSel(payload.sel || null); };
  const onMove = (e) => {
    if (!drag.current) return;
    const m = toModel(e);
    const d = drag.current;
    if (d.type === "wall-pt") {
      const w = weld(m, d.index);
      onChange({ ...model, walls: walls.map((wl, i) => i === d.index ? { ...wl, [d.pt]: { x: w.x, y: w.y } } : wl) });
    } else if (d.type === "wall") {
      // Move the WHOLE wall by dragging its body - translate both endpoints.
      const ddx = m.x - d.startM.x, ddy = m.y - d.startM.y;
      onChange({ ...model, walls: walls.map((wl, i) => i === d.index ? {
        ...wl,
        p1: { x: +(d.p1.x + ddx).toFixed(3), y: +(d.p1.y + ddy).toFixed(3) },
        p2: { x: +(d.p2.x + ddx).toFixed(3), y: +(d.p2.y + ddy).toFixed(3) },
      } : wl) });
    } else if (d.type === "column") {
      onChange({ ...model, columns: columns.map((c, i) => i === d.index ? { ...c, centre: { x: m.x, y: m.y } } : c) });
    } else if (d.type === "opening") {
      const o = openings[d.index]; const w = walls.find((x) => x.id === o.wall_id); if (!w) return;
      const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y, L = Math.hypot(dx, dy) || 1;
      const t = ((m.x - w.p1.x) * dx + (m.y - w.p1.y) * dy) / (L * L);
      const off = Math.max(Number(o.width) / 2, Math.min(L - Number(o.width) / 2, t * L));
      onChange({ ...model, openings: openings.map((x, i) => i === d.index ? { ...x, offset_from_p1: Math.round(off * 10) / 10 } : x) });
    } else if (d.type === "vent") {
      onChange({ ...model, room_labels_pos: (model.room_labels_pos || []).map((z, i) => i === d.index ? { ...z, x: m.x, y: m.y } : z) });
    }
  };
  const onUp = () => { if (drag.current) setHistory((h) => [...h.slice(-40), model]); drag.current = null; };

  const del = () => {
    if (!sel) return;
    if (sel.type === "wall-pt") commit({ ...model, walls: walls.filter((_, i) => i !== sel.index), openings: openings.filter((o) => o.wall_id !== walls[sel.index]?.id) });
    else if (sel.type === "column") commit({ ...model, columns: columns.filter((_, i) => i !== sel.index) });
    else if (sel.type === "opening") commit({ ...model, openings: openings.filter((_, i) => i !== sel.index) });
    else if (sel.type === "vent") commit({ ...model, room_labels_pos: (model.room_labels_pos || []).filter((_, i) => i !== sel.index) });
    setSel(null);
  };

  // ── numeric mutations ──
  const setWallLen = (i, len) => {
    const w = walls[i]; const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y; const L = Math.hypot(dx, dy) || 1;
    const ux = L ? dx / L : 1, uy = L ? dy / L : 0;
    commit({ ...model, walls: walls.map((x, j) => j === i ? { ...x, p2: { x: +(w.p1.x + ux * len).toFixed(3), y: +(w.p1.y + uy * len).toFixed(3) } } : x) });
  };
  const setWallThk = (i, t) => commit({ ...model, walls: walls.map((x, j) => j === i ? { ...x, thickness: t } : x) });
  const setWallType = (i) => commit({ ...model, walls: walls.map((x, j) => j === i ? { ...x, wall_type: (x.wall_type === "exterior" ? "interior" : "exterior") } : x) });

  // Click-to-measure: perpendicular gap from a selected wall to its nearest
  // PARALLEL neighbour on each side (like an AutoCAD dimension). Returns up to
  // two segments in MODEL coordinates.
  const neighborDims = (i) => {
    const w = walls[i]; if (!w) return [];
    const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y;
    const vert = Math.abs(dy) >= Math.abs(dx);
    const out = [];
    if (vert) {
      const x = (w.p1.x + w.p2.x) / 2;
      const y0 = Math.min(w.p1.y, w.p2.y), y1 = Math.max(w.p1.y, w.p2.y);
      for (const dir of [-1, 1]) {
        let best = null;
        walls.forEach((o, j) => {
          if (j === i) return;
          if (Math.abs(o.p2.y - o.p1.y) < Math.abs(o.p2.x - o.p1.x)) return;
          const ox = (o.p1.x + o.p2.x) / 2;
          const oy0 = Math.min(o.p1.y, o.p2.y), oy1 = Math.max(o.p1.y, o.p2.y);
          if (Math.min(y1, oy1) - Math.max(y0, oy0) <= 0.1) return;
          const gap = (ox - x) * dir;
          if (gap > 0.05 && (!best || gap < best.gap)) best = { gap, ox, lo: Math.max(y0, oy0), hi: Math.min(y1, oy1) };
        });
        if (best) { const my = (best.lo + best.hi) / 2; out.push({ x1: x, y1: my, x2: best.ox, y2: my, label: best.gap.toFixed(2) }); }
      }
    } else {
      const y = (w.p1.y + w.p2.y) / 2;
      const x0 = Math.min(w.p1.x, w.p2.x), x1 = Math.max(w.p1.x, w.p2.x);
      for (const dir of [-1, 1]) {
        let best = null;
        walls.forEach((o, j) => {
          if (j === i) return;
          if (Math.abs(o.p2.x - o.p1.x) < Math.abs(o.p2.y - o.p1.y)) return;
          const oy = (o.p1.y + o.p2.y) / 2;
          const ox0 = Math.min(o.p1.x, o.p2.x), ox1 = Math.max(o.p1.x, o.p2.x);
          if (Math.min(x1, ox1) - Math.max(x0, ox0) <= 0.1) return;
          const gap = (oy - y) * dir;
          if (gap > 0.05 && (!best || gap < best.gap)) best = { gap, oy, lo: Math.max(x0, ox0), hi: Math.min(x1, ox1) };
        });
        if (best) { const mx = (best.lo + best.hi) / 2; out.push({ x1: mx, y1: y, x2: mx, y2: best.oy, label: best.gap.toFixed(2) }); }
      }
    }
    return out;
  };
  const setCol = (i, patch) => commit({ ...model, columns: columns.map((c, j) => j === i ? { ...c, ...patch } : c) });
  const setOpen = (i, patch) => commit({ ...model, openings: openings.map((o, j) => j === i ? { ...o, ...patch } : o) });
  const setVent = (i, patch) => commit({ ...model, room_labels_pos: (model.room_labels_pos || []).map((z, j) => j === i ? { ...z, ...patch } : z) });

  const tools = [["select", MousePointer2, "Select"], ["wall", Minus, "Wall"], ["column", Square, "Column"], ["door", DoorOpen, "Door"], ["window", AppWindow, "Window"], ["vent", Wind, "منور"]];

  // ── selection properties panel ──
  let panel = null;
  if (sel?.type === "wall-pt" && walls[sel.index]) {
    const w = walls[sel.index]; const L = Math.hypot(w.p2.x - w.p1.x, w.p2.y - w.p1.y);
    panel = (
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-primary/10 px-3 py-2">
        <span className="text-xs font-black text-primary">Wall</span>
        <NumField label="Length (m)" value={+L.toFixed(2)} step={0.1} onCommit={(v) => setWallLen(sel.index, v)} testid="prop-wall-length" />
        <NumField label="Thickness (m)" value={Number(w.thickness) || 0.15} step={0.05} onCommit={(v) => setWallThk(sel.index, v)} testid="prop-wall-thickness" />
        <button onClick={() => setWallType(sel.index)} data-testid="prop-wall-type" className="rounded-md border border-border px-2 py-1 text-xs font-bold">{w.wall_type === "exterior" ? "Exterior" : "Interior"}</button>
      </div>
    );
  } else if (sel?.type === "column" && columns[sel.index]) {
    const c = columns[sel.index];
    panel = (
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-primary/10 px-3 py-2">
        <span className="text-xs font-black text-primary">Column</span>
        <NumField label="Width (m)" value={c.width || 0.4} step={0.05} onCommit={(v) => setCol(sel.index, { width: v })} testid="prop-col-width" />
        <NumField label="Depth (m)" value={c.depth || 0.4} step={0.05} onCommit={(v) => setCol(sel.index, { depth: v })} testid="prop-col-depth" />
        <button onClick={() => setCol(sel.index, { angle: ((c.angle || 0) + 15) % 360 })} data-testid="prop-col-rotate" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-bold"><RotateCw size={12} /> {(c.angle || 0)}°</button>
      </div>
    );
  } else if (sel?.type === "opening" && openings[sel.index]) {
    const o = openings[sel.index];
    panel = (
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-primary/10 px-3 py-2">
        <span className="text-xs font-black text-primary">Opening</span>
        <NumField label="Width (m)" value={o.width || 0.9} step={0.1} onCommit={(v) => setOpen(sel.index, { width: v })} testid="prop-open-width" />
        <button onClick={() => setOpen(sel.index, { kind: o.kind === "door" ? "window" : "door" })} data-testid="prop-open-kind" className="rounded-md border border-border px-2 py-1 text-xs font-bold">{o.kind === "door" ? "Door" : "Window"}</button>
      </div>
    );
  } else if (sel?.type === "vent" && (model.room_labels_pos || [])[sel.index]) {
    const v = model.room_labels_pos[sel.index];
    panel = (
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-primary/10 px-3 py-2">
        <span className="text-xs font-black text-primary">منور / Light well</span>
        <NumField label="Width (m)" value={v.w || 1} step={0.5} onCommit={(val) => setVent(sel.index, { w: val })} testid="prop-vent-w" />
        <NumField label="Depth (m)" value={v.h || 1} step={0.5} onCommit={(val) => setVent(sel.index, { h: val })} testid="prop-vent-h" />
        <span className="text-xs font-bold text-muted-foreground">{((v.w || 1) * (v.h || 1)).toFixed(1)} م²</span>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {DISCIPLINES.map(([k, ic, en]) => (
          <button key={k} onClick={() => { setDiscipline(k); setMepTool(""); setTool("select"); setPending(null); }}
            data-testid={`web-discipline-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-black ${discipline === k ? "border-primary bg-primary/20 text-primary" : "border-border hover:border-primary/40"}`}>
            <span>{ic}</span> {en}
          </button>
        ))}
      </div>
      {discipline === "arch" ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {tools.map(([k, Icon, en]) => (
            <button key={k} onClick={() => { setTool(k); setPending(null); }} data-testid={`tool-${k}`}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold ${tool === k ? "border-primary bg-primary/15 text-primary" : "border-border hover:border-primary/40"}`}>
              <Icon size={14} /> {en}
            </button>
          ))}
          <div className="mx-1 h-5 w-px bg-border" />
          <button onClick={() => setSnapOn((s) => !s)} data-testid="tool-snap" className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold ${snapOn ? "border-accent bg-accent/15 text-accent" : "border-border"}`}><Magnet size={14} /> Snap 0.25m</button>
          <button onClick={undo} disabled={!history.length} data-testid="tool-undo" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-bold disabled:opacity-40"><Undo2 size={14} /> Undo</button>
          <button onClick={del} disabled={!sel} data-testid="tool-delete" className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/50 px-3 py-1.5 text-xs font-bold text-red-400 disabled:opacity-40"><Trash2 size={14} /> Delete</button>
        </div>
      ) : (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-black text-muted-foreground">{DISCIPLINES.find((d) => d[0] === discipline)?.[2]} tools:</span>
          {(FIXTURE_TOOLS[discipline] || []).map(([k, code, c]) => (
            <button key={k} onClick={() => setMepTool(mepTool === k ? "" : k)} data-testid={`web-fixture-${k}`}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold ${mepTool === k ? "text-white" : "border-border hover:border-primary/40"}`}
              style={mepTool === k ? { background: c, borderColor: c } : undefined}>
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: c }} /> {code}
            </button>
          ))}
          <button onClick={() => setMepTool(mepTool === "delete" ? "" : "delete")} data-testid="web-fixture-delete"
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold ${mepTool === "delete" ? "border-red-500 bg-red-500 text-white" : "border-destructive/50 text-red-400"}`}><Trash2 size={14} /> Delete</button>
          <span className="text-[11px] text-muted-foreground">{mepTool && mepTool !== "delete" ? "Click on the plan to place" : mepTool === "delete" ? "Click a fixture to remove" : "Pick a tool, then click the plan"}</span>
        </div>
      )}
      {panel && <div className="mb-3">{panel}</div>}

      <div className="abl-scroll overflow-auto rounded-xl border border-border bg-white">
        <svg ref={svgRef} width={W} height={H} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} style={{ touchAction: "none", cursor: tool === "select" ? "default" : "crosshair" }}>
          {Array.from({ length: 40 }).map((_, i) => <line key={`gx${i}`} x1={i * (W / 40)} y1={0} x2={i * (W / 40)} y2={H} stroke="#EEF2F7" strokeWidth={1} />)}
          {Array.from({ length: 28 }).map((_, i) => <line key={`gy${i}`} x1={0} y1={i * (H / 28)} x2={W} y2={i * (H / 28)} stroke="#EEF2F7" strokeWidth={1} />)}
          {zones.map((z, i) => <text key={`z${i}`} x={px(z.x)} y={py(z.y)} fontSize={11} fontWeight="600" fill="#334155" textAnchor="middle">{z.label}</text>)}
          {/* ventilation light-wells - draggable/selectable shafts with windows */}
          {vents.map(({ v, i }) => {
            const active = sel?.type === "vent" && sel.index === i;
            const cx = px(v.x), cy = py(v.y);
            const ww = (v.w || 1) * tf.s, hh = (v.h || 1) * tf.s;
            const x = cx - ww / 2, y = cy - hh / 2;
            return (
              <g key={`vent${i}`} onPointerDown={(e) => startDrag({ type: "vent", index: i, sel: { type: "vent", index: i } }, e)} style={{ cursor: "grab" }}>
                <rect x={x} y={y} width={ww} height={hh} fill="#E0F2FE" stroke={active ? "#00B4D8" : "#0EA5E9"} strokeWidth={active ? 2.5 : 1.5} />
                <line x1={x} y1={y} x2={x + ww} y2={y + hh} stroke="#0EA5E9" strokeWidth={0.8} />
                <line x1={x + ww} y1={y} x2={x} y2={y + hh} stroke="#0EA5E9" strokeWidth={0.8} />
                <line x1={x + ww * 0.3} y1={y} x2={x + ww * 0.7} y2={y} stroke="#2563EB" strokeWidth={3} />
                <line x1={x + ww * 0.3} y1={y + hh} x2={x + ww * 0.7} y2={y + hh} stroke="#2563EB" strokeWidth={3} />
                <line x1={x} y1={y + hh * 0.3} x2={x} y2={y + hh * 0.7} stroke="#2563EB" strokeWidth={3} />
                <line x1={x + ww} y1={y + hh * 0.3} x2={x + ww} y2={y + hh * 0.7} stroke="#2563EB" strokeWidth={3} />
                <text x={cx} y={cy} fontSize={9} fontWeight="800" textAnchor="middle" dominantBaseline="middle" fill="#0369A1" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>منور {((v.w || 1) * (v.h || 1)).toFixed(1)}م²</text>
              </g>
            );
          })}
          {walls.map((w, i) => {
            const isExt = (w.wall_type || "").toLowerCase() === "exterior";
            const active = sel?.type === "wall-pt" && sel.index === i;
            return (
              <g key={`w${i}`}>
                <line x1={px(w.p1.x)} y1={py(w.p1.y)} x2={px(w.p2.x)} y2={py(w.p2.y)} stroke={active ? "#00B4D8" : isExt ? "#0F172A" : "#475569"} strokeWidth={Math.max(3, (Number(w.thickness) || 0.2) * tf.s)} strokeLinecap="round" />
                {/* wide invisible hit-line: click selects the wall + shows its
                    measurements; drag MOVES the whole wall. */}
                <line x1={px(w.p1.x)} y1={py(w.p1.y)} x2={px(w.p2.x)} y2={py(w.p2.y)} stroke="transparent" strokeWidth={18} style={{ cursor: tool === "select" ? "move" : "inherit" }} data-testid={`wall-hit-${i}`} onPointerDown={(e) => { if (tool === "select") { e.stopPropagation(); setSel({ type: "wall-pt", index: i, pt: "p1" }); const sm = toModel(e); drag.current = { type: "wall", index: i, startM: sm, p1: { ...w.p1 }, p2: { ...w.p2 } }; } }} />
              </g>
            );
          })}
          {/* click-to-measure: distance from the selected wall (or the wall
           *  under a selected door/window/vent) to its nearest neighbour. */}
          {(() => {
            let mi = null;
            if (sel?.type === "wall-pt") mi = sel.index;
            else if (sel?.type === "opening" && openings[sel.index]) {
              const wid = openings[sel.index].wall_id;
              const idx = walls.findIndex((w) => w.id === wid);
              if (idx >= 0) mi = idx;
            }
            if (mi === null || !walls[mi]) return null;
            return neighborDims(mi).map((d, k) => {
              const X1 = px(d.x1), Y1 = py(d.y1), X2 = px(d.x2), Y2 = py(d.y2);
              const mx = (X1 + X2) / 2, my = (Y1 + Y2) / 2;
              return (
                <g key={`nd${k}`} style={{ pointerEvents: "none" }}>
                  <line x1={X1} y1={Y1} x2={X2} y2={Y2} stroke="#DC2626" strokeWidth={1.5} strokeDasharray="5 3" />
                  <circle cx={X1} cy={Y1} r={2.5} fill="#DC2626" />
                  <circle cx={X2} cy={Y2} r={2.5} fill="#DC2626" />
                  <text x={mx} y={my} dy={-4} fontSize={11} fontWeight="900" textAnchor="middle" fill="#DC2626" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>{d.label} م</text>
                </g>
              );
            });
          })()}
          {/* live wall-length labels - recompute on every drag/edit */}
          {walls.map((w, i) => {
            const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y;
            const L = Math.hypot(dx, dy);
            if (L < 0.35) return null;
            const mx = px((w.p1.x + w.p2.x) / 2), my = py((w.p1.y + w.p2.y) / 2);
            let a = (Math.atan2(py(w.p2.y) - py(w.p1.y), px(w.p2.x) - px(w.p1.x)) * 180) / Math.PI;
            if (a > 90 || a < -90) a += 180;
            return <text key={`wl${i}`} x={mx} y={my} fontSize={10} fontWeight="700" fill="#0F172A" textAnchor="middle" dy={-4} transform={`rotate(${a} ${mx} ${my})`} style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>{L.toFixed(2)} m</text>;
          })}
          {openings.map((o, i) => {
            const w = walls.find((x) => x.id === o.wall_id); if (!w) return null;
            const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y, L = Math.hypot(dx, dy) || 1;
            const t = (Number(o.offset_from_p1) || 0) / L;
            const cx = w.p1.x + t * dx, cy = w.p1.y + t * dy;
            if (![cx, cy].every(F)) return null;
            const active = sel?.type === "opening" && sel.index === i;
            return <rect key={`o${i}`} x={px(cx) - 6} y={py(cy) - 6} width={12} height={12} rx={2} fill={o.kind === "door" ? "#B45309" : "#0EA5E9"} stroke={active ? "#00B4D8" : "#fff"} strokeWidth={active ? 2.5 : 1} style={{ cursor: "grab" }} onPointerDown={(e) => startDrag({ type: "opening", index: i, sel: { type: "opening", index: i } }, e)} />;
          })}
          {/* live opening-width labels - recompute on every drag/edit */}
          {openings.map((o, i) => {
            const w = walls.find((x) => x.id === o.wall_id); if (!w) return null;
            const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y, L = Math.hypot(dx, dy) || 1;
            const t = (Number(o.offset_from_p1) || 0) / L;
            const cx = w.p1.x + t * dx, cy = w.p1.y + t * dy;
            if (![cx, cy].every(F) || !o.width) return null;
            return <text key={`ow${i}`} x={px(cx)} y={py(cy)} dy={16} fontSize={9} fontWeight="800" textAnchor="middle" fill={o.kind === "door" ? "#B45309" : "#0369A1"} style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>{Number(o.width).toFixed(2)}</text>;
          })}
          {/* overall frontage + depth dimension chains - live */}
          {walls.length >= 2 && (() => {
            const xs = walls.flatMap((w) => [w.p1.x, w.p2.x]);
            const ys = walls.flatMap((w) => [w.p1.y, w.p2.y]);
            const xmin = Math.min(...xs), xmax = Math.max(...xs);
            const ymin = Math.min(...ys), ymax = Math.max(...ys);
            if (![xmin, xmax, ymin, ymax].every(F)) return null;
            const topY = py(ymin) - 22, leftX = px(xmin) - 30;
            return (
              <g>
                <line x1={px(xmin)} y1={topY} x2={px(xmax)} y2={topY} stroke="#0F172A" strokeWidth={1.2} />
                <line x1={px(xmin)} y1={topY - 4} x2={px(xmin)} y2={topY + 4} stroke="#0F172A" strokeWidth={1.5} />
                <line x1={px(xmax)} y1={topY - 4} x2={px(xmax)} y2={topY + 4} stroke="#0F172A" strokeWidth={1.5} />
                <text x={(px(xmin) + px(xmax)) / 2} y={topY - 5} fontSize={11} fontWeight="900" textAnchor="middle" fill="#0F172A" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>{(xmax - xmin).toFixed(2)} m</text>
                <line x1={leftX} y1={py(ymin)} x2={leftX} y2={py(ymax)} stroke="#0F172A" strokeWidth={1.2} />
                <line x1={leftX - 4} y1={py(ymin)} x2={leftX + 4} y2={py(ymin)} stroke="#0F172A" strokeWidth={1.5} />
                <line x1={leftX - 4} y1={py(ymax)} x2={leftX + 4} y2={py(ymax)} stroke="#0F172A" strokeWidth={1.5} />
                <text x={leftX - 6} y={(py(ymin) + py(ymax)) / 2} fontSize={11} fontWeight="900" textAnchor="middle" fill="#0F172A" transform={`rotate(-90 ${leftX - 6} ${(py(ymin) + py(ymax)) / 2})`} style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>{(ymax - ymin).toFixed(2)} m</text>
              </g>
            );
          })()}
          {columns.map((c, i) => {
            if (!c.centre || ![c.centre.x, c.centre.y].every(F)) return null;
            const active = sel?.type === "column" && sel.index === i;
            const cxp = px(c.centre.x), cyp = py(c.centre.y);
            const ww = Math.max(8, (c.width || 0.4) * tf.s), hh = Math.max(8, (c.depth || 0.4) * tf.s);
            return <rect key={`c${i}`} x={cxp - ww / 2} y={cyp - hh / 2} width={ww} height={hh} transform={`rotate(${c.angle || 0} ${cxp} ${cyp})`} fill="#1E293B" stroke={active ? "#00B4D8" : "#0EA5E9"} strokeWidth={active ? 2.5 : 1} style={{ cursor: "grab" }} onPointerDown={(e) => startDrag({ type: "column", index: i, sel: { type: "column", index: i } }, e)} />;
          })}
          {walls.map((w, i) => ["p1", "p2"].map((pt) => (
            <g key={`h${i}${pt}`}>
              <circle cx={px(w[pt].x)} cy={py(w[pt].y)} r={14} fill="transparent" style={{ cursor: "grab" }} onPointerDown={(e) => startDrag({ type: "wall-pt", index: i, pt, sel: { type: "wall-pt", index: i } }, e)} />
              <circle cx={px(w[pt].x)} cy={py(w[pt].y)} r={6} fill="#fff" stroke="#00B4D8" strokeWidth={2} style={{ pointerEvents: "none" }} />
            </g>
          )))}
          {discipline !== "arch" && fixtures
            .filter((f) => (FIXTURE_TOOLS[discipline] || []).some(([k]) => k === f.kind))
            .map((f, i) => {
              if (![f.x, f.y].every(F)) return null;
              const meta = FIXTURE_META[f.kind] || { code: "?", c: "#0891B2" };
              return (
                <g key={`mf${f.id || i}`} data-testid={`web-fx-${f.id || i}`}
                   style={{ cursor: mepTool === "delete" ? "not-allowed" : "pointer" }}
                   onPointerDown={(e) => { e.stopPropagation(); if (mepTool === "delete") commit({ ...model, mep_fixtures: fixtures.filter((x) => x !== f) }); }}>
                  <circle cx={px(f.x)} cy={py(f.y)} r={9} fill={meta.c} stroke="#fff" strokeWidth={1.5} />
                  <text x={px(f.x)} y={py(f.y)} dy={3} fontSize={8} fontWeight="900" fill="#fff" textAnchor="middle" style={{ pointerEvents: "none" }}>{meta.code}</text>
                </g>
              );
            })}
          {pending && <circle cx={px(pending.x)} cy={py(pending.y)} r={6} fill="none" stroke="#2DD4BF" strokeWidth={2} strokeDasharray="3 2" />}
        </svg>
      </div>
    </div>
  );
}
